import { t } from '@lingui/core/macro';
import type { UserGroupAddressRecord } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { ConfirmAddressesStep } from './ConfirmAddressesStep';
import { ConfirmPromptFooter } from './ConfirmPromptFooter';
import { PERSONAL_INFO_REVIEW_MUTATION_KEY, type InstructionsDraft } from './types';
import { useReviewMutationsPending } from './useReviewMutationsPending';

type Props = {
  userGroupId: number;
  userGroupAddresses: UserGroupAddressRecord[];
  onConfirmed: () => void;
  onSkip: () => void;
};

/**
 * Remove, add and edit the driver notes. Every change here goes to its own
 * endpoint, so this dialog does not stamp the review — the flow does that once
 * the last step is answered, which keeps "stamp only a finished review" in one
 * place rather than spread across whichever dialog happens to be last.
 */
export const ConfirmAddressesDialog = ({ userGroupId, userGroupAddresses, onConfirmed, onSkip }: Props) => {
  const [instructions, setInstructions] = useState<InstructionsDraft>({});
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  // Only this flow's requests, not every mutation in the customer app: an
  // unrelated background mutation must not leave the prompt inexplicably dead.
  const isMutating = useReviewMutationsPending();

  const {
    mutateAsync: patchAddress,
    isPending: isSavingNotes,
    error: saveError
  } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroupAddress,
    queryConfig: { mutationKey: PERSONAL_INFO_REVIEW_MUTATION_KEY }
  });

  const saveChangedAddresses = async () => {
    // Untouched addresses are left out, so no audit entry is written for them.
    const changed = userGroupAddresses.filter((address) => {
      const draft = instructions[address.id];
      return draft !== undefined && draft !== address.instructions;
    });

    for (const address of changed) {
      await patchAddress({ userGroupAddressId: address.id, instructions: instructions[address.id] });
    }
    if (changed.length > 0) {
      await invalidateQuery({ urlKey: URLKeys.getUserGroupAddresses });
    }
  };

  const saveThen = async (onSuccess: () => void) => {
    setIsSavingStep(true);
    try {
      await saveChangedAddresses();
      onSuccess();
    } finally {
      setIsSavingStep(false);
    }
  };

  const isBusy = isMutating || isSavingStep || isResolvingAddress;

  return (
    <NoddiDialog
      open
      // Answered with Confirm or Skip — never by clicking outside the prompt.
      onClose={() => {}}
      showCloseButton={false}
      className='max-h-[85vh]'
      title={t`Your addresses`}
      description={t`Remove the ones you no longer use, and add notes that help the driver.`}
      footer={
        <ConfirmPromptFooter
          errorContent={saveError ? <ApiErrorMessageWithTrans error={saveError} /> : null}
          isBusy={isBusy}
          onSkip={() => void saveThen(onSkip).catch(() => undefined)}
          primaryAction={
            <NoddiButton
              loading={isSavingNotes || isSavingStep}
              disabled={isBusy}
              onClick={() => void saveThen(onConfirmed).catch(() => undefined)}
            >
              {t`Confirm`}
            </NoddiButton>
          }
        />
      }
    >
      <ConfirmAddressesStep
        userGroupId={userGroupId}
        userGroupAddresses={userGroupAddresses}
        instructions={instructions}
        onAddressResolutionChange={setIsResolvingAddress}
        onInstructionsChange={(userGroupAddressId, value) =>
          setInstructions((current) => ({ ...current, [userGroupAddressId]: value }))
        }
      />
    </NoddiDialog>
  );
};
